import { useState } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Truck, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import AddressFields from "../shared/AddressFields";
import { formatPhoneNumber } from "../../lib/formatters";
const SuppliersTab = ({ suppliers, getAuthHeaders, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", contact: "", phone: "", email: "",
    street: "", apt: "", city: "", state: "", zip_code: ""
  });

  const resetForm = () => {
    setForm({ name: "", contact: "", phone: "", email: "", street: "", apt: "", city: "", state: "", zip_code: "" });
    setEditingSupplier(null);
  };

  const openEditSupplier = (s) => {
    setEditingSupplier(s);
    setForm({
      name: s.name || "",
      contact: s.contact || "",
      phone: s.phone || "",
      email: s.email || "",
      street: s.street || "",
      apt: s.apt || "",
      city: s.city || "",
      state: s.state || "",
      zip_code: s.zip_code || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await apiClient.put(`/settings/suppliers/${editingSupplier.id}`, 
          { ...form, id: editingSupplier.id }
        );
        toast.success("Supplier updated");
      } else {
        await apiClient.post(`/settings/suppliers`, 
          { ...form, id: "" }
        );
        toast.success("Supplier added");
      }
      onRefresh();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/settings/suppliers/${id}`);
      toast.success("Supplier deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete supplier");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Suppliers</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-2" />Add Supplier</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSupplier ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-group">
                <Label>Company Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} autoComplete="organization" required placeholder="e.g., XYZ Building Supply" data-testid="supplier-name-input" />
              </div>
              <div className="form-group">
                <Label>Contact Person</Label>
                <Input value={form.contact} onChange={(e) => setForm({...form, contact: e.target.value})} placeholder="e.g., Jane Doe" data-testid="supplier-contact-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: formatPhoneNumber(e.target.value)})} placeholder="(555) 987-6543" maxLength={14} data-testid="supplier-phone-input" />
                </div>
                <div className="form-group">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="e.g., jane@xyz.com" data-testid="supplier-email-input" />
                </div>
              </div>
              
              {/* Address Fields - Using shared component */}
              <AddressFields
                values={{ street: form.street, apt: form.apt, city: form.city, state: form.state, zip_code: form.zip_code }}
                onChange={(field, value) => setForm({...form, [field]: value})}
                onChangeMany={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                prefix="supplier"
                compact={true}
              />
              
              <Button type="submit" className="w-full" data-testid="submit-supplier-btn">
                {editingSupplier ? "Update Supplier" : "Add Supplier"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers..." className="pl-9 pr-9" data-testid="suppliers-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        {suppliers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Company Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppliers.filter((s) => {
                if (!search.trim()) return true;
                const searchable = [s.name, s.contact, s.phone, s.email].filter(Boolean).join(" ");
                return fuzzyMatch(search, searchable);
              }).map((s) => (
                <TableRow key={s.id} className="table-row">
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{s.contact || "-"}</TableCell>
                  <TableCell>{s.phone || "-"}</TableCell>
                  <TableCell>{s.email || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditSupplier(s)} data-testid={`edit-supplier-${s.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-supplier-${s.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Supplier?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove {s.name}.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(s.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No suppliers added yet</p>
            <p className="text-sm mt-1">Add suppliers to use them for invoice tracking</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SuppliersTab;
